import {
  InlineLoading,
  InlineNotification,
  Tab,
  TabList as CarbonTabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@carbon/react';
import { useMemo, useState } from 'react';
import { PackageEvaluationResponse } from 'types/packages-api/responses';

import style from './result-tabs.module.scss';
import { UnitsAllocations } from './units-allocations';
import { Summary } from './summary';
import { Assets } from './assets';
import { InventoryAssets } from './inventory-assets';
import { FreeCashFlow } from './free-cash-flow';
import {
  subscribeToChannel,
  useDownloadResource,
} from 'lib/hooks/useDownloadFile';
import { DownloadButton } from 'components/download-button';
import { PackageSensitivities } from './package-sensitivities';
import { Warning } from '@carbon/react/icons';
import { SlotsAllocations } from './slots-allocations';
import { PackageOverviewCombined } from './package-overview-combined';
import { PackageOverview } from './package-overview';
import { LoadSlotAllocations } from './load-slot-allocations';
import { MapIsolator } from './map-isolator';
import { LoadPackage } from '../page';
import { Wells } from './wells';

import { buildMapPointsFromSlots, buildMapPointsFromUnits } from './utils';
import { useEvaluationDownloadsAtom } from './atoms';
import { useAtom } from 'jotai';
import { Comments } from 'components/events-panel';
import { Slots } from './slots';
import { PartiallyCoveredWells } from './partially-covered-wells';
import { BlobArtifacts } from './blob-artifacts';
import { format0 } from 'utils/number';

const DownloadEvaluationSimple = ({
  downloadHref,
  buttonText,
}: {
  downloadHref: string;
  buttonText: string;
}) => {
  const downloadOwnership = useDownloadResource(downloadHref);

  return (
    <DownloadButton
      loading={downloadOwnership.generatingDownload}
      loadingText="Building export"
      kind="primary"
      onClick={() => downloadOwnership.triggerDownload()}
    >
      {buttonText}
    </DownloadButton>
  );
};

const ExcelExport = ({
  id,
  href,
  buttonText,
}: {
  id: string;
  href: string;
  buttonText: string;
}) => {
  const downloadResource = useDownloadResource(href);
  const downloadsAtom = useEvaluationDownloadsAtom(href);
  const [request, requestDownloads] = useAtom(downloadsAtom);
  if (request.loading) return <InlineLoading />;
  if ('error' in request && request.error) {
    return (
      <InlineNotification
        kind="error"
        title={String(request.error)}
        lowContrast
      />
    );
  }
  if (request.data && !('error' in request.data)) {
    const downloads = request.data;
    if (downloads.length) {
      const lastDownload = downloads[0];
      const isProcessing =
        lastDownload.status === 'processing' ||
        lastDownload.status === 'pending';

      if (isProcessing) {
        subscribeToChannel(
          lastDownload.notifications.channel,
          lastDownload.notifications.events[0]['name'],
          lastDownload.href,
          () => {
            requestDownloads();
          }
        );

        return <InlineLoading description="Building Export" />;
      }
    }

    return (
      <DownloadButton
        id={id}
        loading={downloadResource.generatingDownload}
        loadingText="Building export"
        kind="primary"
        onClick={() => {
          if (!downloads.length) {
            downloadResource.triggerDownload((processing) => {
              if (!processing) {
                requestDownloads();
              }
            });
          } else {
            window.open(downloads[0].fileUrl, '_blank', 'noopener,noreferrer');
          }
        }}
      >
        {buttonText}
      </DownloadButton>
    );
  }

  return null;
};

const ResultTabs = ({
  evaluation,
  isCombined = false,
}: {
  evaluation: PackageEvaluationResponse;
  isCombined?: boolean;
}) => {
  return (
    <div className={style.resultTab}>
      <div className={style.titleArea}>
        <div className={style.summaryData}>
          <Summary data={evaluation} isCombined={isCombined} />
        </div>
        <div className={style.downloadButtons}>
          <LoadPackage
            render={(packageData) => {
              return (
                <LoadSlotAllocations
                  slotsAllocationsHref={
                    evaluation.resources.slotsAllocations.href
                  }
                  unitHref={evaluation.resources.allocations.href}
                  renderUnits={(allocations) => {
                    const mapPoints = buildMapPointsFromUnits(
                      allocations || []
                    );
                    return (
                      <MapIsolator
                        packageData={packageData}
                        mapPoints={mapPoints}
                        type="units"
                      />
                    );
                  }}
                  renderSlots={(allocations) => {
                    const mapPoints = buildMapPointsFromSlots(
                      allocations || []
                    );
                    return (
                      <MapIsolator
                        packageData={packageData}
                        mapPoints={mapPoints}
                        type="slots"
                      />
                    );
                  }}
                />
              );
            }}
          />
          {evaluation.resources.evaluationDetailsDownload && (
            <ExcelExport
              id="package-exports"
              href={evaluation.resources.evaluationDetailsDownload.href}
              buttonText="Evaluation Details"
            />
          )}
          {evaluation.resources.inventoryAssetsDownload && (
            <DownloadEvaluationSimple
              downloadHref={evaluation.resources.inventoryAssetsDownload.href}
              buttonText="Inventory Assets"
            />
          )}
          {evaluation.resources.cashflowsBySubtractDownload && (
            <DownloadEvaluationSimple
              downloadHref={
                evaluation.resources.cashflowsBySubtractDownload.href
              }
              buttonText="Cashflows By Subtract"
            />
          )}
          {evaluation.resources.kpiDownload && (
            <DownloadEvaluationSimple
              downloadHref={evaluation.resources.kpiDownload.href}
              buttonText="Eric's Query"
            />
          )}
        </div>
      </div>
      <TabList evaluation={evaluation} isCombined={isCombined} />
    </div>
  );
};

function TabList({
  evaluation,
  isCombined = false,
}: {
  evaluation: PackageEvaluationResponse;
  isCombined: boolean;
}) {
  const [selected, setSelected] = useState(0);
  const [eventsCount, setEventsCount] = useState(evaluation.eventsCount);

  // Array-driven tabs to prevent Tab/TabPanel index mismatch when
  // conditional tabs (Wells) are absent. Same pattern as entity-tabs.tsx.
  const tabs = useMemo(() => {
    const list: {
      key: string;
      label: React.ReactNode;
      render: () => React.ReactNode;
    }[] = [
      {
        key: 'package-overview',
        label: 'Package Overview',
        render: () => {
          return isCombined ? (
            <PackageOverviewCombined
              href={evaluation.resources.packageOverview.href}
            />
          ) : (
            <PackageOverview href={evaluation.resources.packageOverview.href} />
          );
        },
      },
      {
        key: 'assets',
        label: 'Assets',
        render: () => {
          return evaluation.resources.inventoryAssets?.href ? (
            <InventoryAssets href={evaluation.resources.inventoryAssets.href} />
          ) : (
            <Assets href={evaluation.resources.assets.href} />
          );
        },
      },
      {
        key: 'unit-allocations',
        label: (
          <>
            Unit Allocations
            {evaluation.resources.allocations.hasWarning && (
              <span className={style.warningIcon}>
                <Warning />
              </span>
            )}
          </>
        ),
        render: () => (
          <UnitsAllocations href={evaluation.resources.allocations.href} />
        ),
      },
      {
        key: 'slots',
        label: 'Slots',
        render: () => <Slots slotsHref={evaluation.resources.slots.href} />,
      },
      {
        key: 'slots-allocations',
        label: 'Slots Allocations',
        render: () => (
          <SlotsAllocations href={evaluation.resources.slotsAllocations.href} />
        ),
      },
      {
        key: 'free-cash-flow',
        label: 'Free Cash Flow',
        render: () => (
          <FreeCashFlow href={evaluation.resources.valuationOverview.href} />
        ),
      },
      {
        key: 'sensitivities',
        label: 'Sensitivities',
        render: () => (
          <PackageSensitivities
            href={evaluation.resources.sensitivitiesOverview.href}
          />
        ),
      },
    ];

    if (evaluation.resources.wells) {
      list.push({
        key: 'wells',
        label: 'Wells',
        render: () => <Wells href={evaluation.resources.wells!.href} />,
      });
    }

    list.push(
      {
        key: 'comments',
        label: `Comments (${eventsCount})`,
        render: () => (
          <Comments
            eventsHref={evaluation.resources.events.href}
            commentsHref={evaluation.resources.comments.href}
            onComment={() => setEventsCount((c) => c + 1)}
          />
        ),
      },
      {
        key: 'partially-covered-wells',
        label: (
          <span
            className={
              evaluation.partiallyCoveredWellsCount > 0
                ? style.warningCount
                : undefined
            }
          >
            Partially Covered Wells (
            {format0(evaluation.partiallyCoveredWellsCount)})
          </span>
        ),
        render: () => (
          <PartiallyCoveredWells
            href={evaluation.resources.partiallyCoveredWells.href}
          />
        ),
      },
      {
        key: 'blob-artifacts',
        label: 'Blob Artifacts',
        render: () => (
          <BlobArtifacts href={evaluation.resources.blobArtifacts.href} />
        ),
      }
    );

    return list;
  }, [evaluation, isCombined, eventsCount]);

  return (
    <div className={style.tabsContained}>
      <Tabs
        selectedIndex={selected}
        onChange={({ selectedIndex }) => setSelected(selectedIndex)}
      >
        <CarbonTabList contained>
          {tabs.map((tab) => (
            <Tab key={tab.key}>{tab.label}</Tab>
          ))}
        </CarbonTabList>
        <TabPanels>
          {tabs.map((tab, index) => (
            <TabPanel key={tab.key}>
              {selected === index && tab.render()}
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </div>
  );
}

export { ResultTabs };
